<template>
  <table>
    <thead>
      <bm-grid-table-head
        :visible-columns="visibleColumns"
        :sort-by="sortBy"
        :allow-sort="allowSort"
        :allow-select="allowSelect"
        :allow-drop="allowDrop"
        :first-column-indent-px="getTableViewFirstColumnIndentPx()"
        :header-select-checked="headerSelectChecked"
        :header-select-disabled="headerSelectDisabled"
        :sub-grid="subGrid"
        @toggle-select-all="emit('toggle-select-all')"
        @sort-column="emit('sort-column', $event)"
        @drop="emit('drop', { row: null, event: $event })"
      />
    </thead>
    <tbody>
      <bm-grid-no-data
        v-if="virtualGrid.length === 0"
        variant="table"
        :colspan="columnCount"
      >
        <slot name="no-data" />
      </bm-grid-no-data>

      <template v-else-if="!hasGrouping">
        <template v-for="(row, index) in displayContentRows">
          <bm-grid-table-content-row
            :key="row.rowKey"
            :row="row"
            :row-index="index"
            :visible-columns="visibleColumns"
            :allow-drag="allowDrag"
            :allow-drop="allowDrop"
            :allow-expand="allowExpand"
            :allow-select="allowSelect"
            :draggable-items="draggableItems"
            :first-column-indent-px="getTableViewFirstColumnIndentPx(row, 0)"
            :row-draggable="rowDraggable"
            :row-expandable="rowExpandable"
            :row-selectable="rowSelectable"
            :row-classes="rowClasses"
            :row-click-route="rowClickRoute"
            :is-row-clickable="isRowClickable"
            :allow-row-click-route-on-first-cell="allowRowClickRouteOnFirstCell"
            @drag-start="emit('drag-start', row)"
            @toggle-expand="emit('toggle-expand', row)"
            @toggle-select="emit('toggle-select', row)"
            @drop="emit('drop', { row, event: $event })"
            @dragover="emit('dragover', row)"
            @row-click="emit('row-click', row)"
          />
          <bm-grid-table-expandable-row
            :key="`${row.rowKey}-expand`"
            :row="row"
            :row-index="index"
            :column-count="columnCount"
            :allow-expand="allowExpand"
            :has-expand-slot="hasExpandSlot"
            :row-expandable="rowExpandable"
          >
            <slot name="expand" v-bind="row" />
          </bm-grid-table-expandable-row>
        </template>
        <bm-grid-show-all-rows
          v-if="showAllRemaining != null"
          :key="'show-all'"
          :remaining-rows="showAllRemaining"
          variant="table"
          :colspan="columnCount"
          @show-all="emit('show-all')"
        />
      </template>

      <!-- Flattened group/content rows: no multi-tr wrapper SFC (breaks tbody). -->
      <template v-else>
        <template v-for="(node, nodeIndex) in flattenedGroupNodes">
          <bm-grid-table-group-row
            v-if="node.kind === 'group'"
            :key="`${node.row.rowKey}-group`"
            :group="node.row"
            :depth="node.depth"
            :visible-columns="visibleColumns"
            :grouped-columns="groupedColumns"
            :allow-select="allowSelect"
            :first-column-indent-px="
              getTableViewFirstColumnIndentPx(node.row, node.depth)
            "
            :row-selectable="rowSelectable"
            @toggle-expand="emit('toggle-group-expand', node.row)"
            @toggle-select="emit('toggle-select', node.row)"
          />
          <template v-else-if="node.kind === 'content'">
            <bm-grid-table-content-row
              :key="node.row.rowKey"
              :row="node.row"
              :row-index="node.rowIndex"
              :visible-columns="visibleColumns"
              :allow-drag="allowDrag"
              :allow-drop="allowDrop"
              :allow-expand="allowExpand"
              :allow-select="allowSelect"
              :draggable-items="draggableItems"
              :first-column-indent-px="
                getTableViewFirstColumnIndentPx(node.row, node.depth)
              "
              :row-draggable="rowDraggable"
              :row-expandable="rowExpandable"
              :row-selectable="rowSelectable"
              :row-classes="rowClasses"
              :row-click-route="rowClickRoute"
              :is-row-clickable="isRowClickable"
              :allow-row-click-route-on-first-cell="
                allowRowClickRouteOnFirstCell
              "
              @drag-start="emit('drag-start', node.row)"
              @toggle-expand="emit('toggle-expand', node.row)"
              @toggle-select="emit('toggle-select', node.row)"
              @drop="emit('drop', { row: node.row, event: $event })"
              @dragover="emit('dragover', node.row)"
              @row-click="emit('row-click', node.row)"
            />
            <bm-grid-table-expandable-row
              :key="`${node.row.rowKey}-expand`"
              :row="node.row"
              :row-index="node.rowIndex"
              :column-count="columnCount"
              :allow-expand="allowExpand"
              :has-expand-slot="hasExpandSlot"
              :row-expandable="rowExpandable"
            >
              <slot name="expand" v-bind="node.row" />
            </bm-grid-table-expandable-row>
          </template>
          <bm-grid-show-all-rows
            v-else
            :key="`show-all-${node.depth}-${nodeIndex}`"
            :remaining-rows="node.remaining"
            variant="table"
            :colspan="columnCount"
            @show-all="emit('show-all')"
          />
        </template>
      </template>
    </tbody>
    <bm-grid-table-footer :visible-columns="visibleColumns" />
  </table>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import type {
  BmGridColumnConfigurations,
  BmGridColumnSortState,
  ContentRow,
  Row,
  RowGroup,
} from "@/components/controls/grid/BmGridData";
import { isContentRow } from "@/components/controls/grid/BmGridData";
import BmGridNoData from "@/components/controls/grid/BmGridNoData.vue";
import BmGridShowAllRows from "@/components/controls/grid/BmGridShowAllRows.vue";
import BmGridTableContentRow from "@/components/controls/grid/BmGridTableContentRow.vue";
import BmGridTableExpandableRow from "@/components/controls/grid/BmGridTableExpandableRow.vue";
import BmGridTableFooter from "@/components/controls/grid/BmGridTableFooter.vue";
import BmGridTableGroupRow from "@/components/controls/grid/BmGridTableGroupRow.vue";
import BmGridTableHead from "@/components/controls/grid/BmGridTableHead.vue";
import type { DnDEvent } from "vue-easy-dnd";
import type { Location } from "vue-router";

/**
 * Hand-rolled desktop table view for BmGrid (thead / tbody / footer).
 * Emits semantic events; parent owns virtual-grid mutation and refresh.
 */

type FlatGroupNode =
  | { kind: "group"; row: RowGroup; depth: number }
  | { kind: "content"; row: ContentRow; depth: number; rowIndex: number }
  | { kind: "show-all"; remaining: number; depth: number };

interface BmGridTableViewProps {
  virtualGrid: readonly Row[];
  visibleColumns: BmGridColumnConfigurations;
  groupedColumns: BmGridColumnConfigurations;
  sortBy: readonly BmGridColumnSortState[];
  allowSort: boolean;
  allowSelect: boolean;
  allowDrag: boolean;
  allowDrop: boolean;
  allowExpand: boolean;
  subGrid: boolean;
  draggableItems: readonly string[];
  visibleRowLimit?: number | null;
  rowDraggable: (item: unknown) => boolean;
  rowExpandable: (item: unknown) => boolean;
  rowSelectable: (item: unknown) => boolean;
  rowClasses: (row: unknown) => string[];
  rowClickRoute?: ((item: unknown) => Location | null) | null;
  isRowClickable: boolean;
  allowRowClickRouteOnFirstCell: boolean;
}

const props = withDefaults(defineProps<BmGridTableViewProps>(), {
  visibleRowLimit: null,
  rowClickRoute: null,
});

interface BmGridTableViewEvents {
  (event: "toggle-select-all"): void;
  (event: "toggle-select", row: Row): void;
  (
    event: "sort-column",
    payload: { columnKey: string; shiftKey: boolean }
  ): void;
  (event: "drop", payload: { row: Row | null; event: DnDEvent }): void;
  (event: "dragover", row: ContentRow): void;
  (event: "drag-start", row: ContentRow): void;
  (event: "toggle-expand", row: ContentRow): void;
  (event: "toggle-group-expand", group: RowGroup): void;
  (event: "show-all"): void;
  (event: "row-click", row: ContentRow): void;
}

const emit = defineEmits<BmGridTableViewEvents>();

const slots = useSlots();

const hasGrouping = computed(
  () => Object.keys(props.groupedColumns).length > 0
);

const columnCount = computed(() => Object.keys(props.visibleColumns).length);

const hasExpandSlot = computed(() => !!slots.expand);

const headerSelectChecked = computed(() =>
  props.virtualGrid.length
    ? props.virtualGrid.every((r) => r.isSelected || !props.rowSelectable(r))
    : false
);

const headerSelectDisabled = computed(() => !props.virtualGrid.length);

const contentRows = computed(
  () => props.virtualGrid.filter((r) => r.rowType === "content") as ContentRow[]
);

const displayContentRows = computed(() => {
  if (
    props.visibleRowLimit != null &&
    contentRows.value.length > props.visibleRowLimit
  ) {
    return contentRows.value.slice(0, props.visibleRowLimit);
  }
  return contentRows.value;
});

const showAllRemaining = computed((): number | null => {
  if (
    props.visibleRowLimit != null &&
    contentRows.value.length > props.visibleRowLimit
  ) {
    return contentRows.value.length - props.visibleRowLimit;
  }
  return null;
});

const flattenedGroupNodes = computed((): FlatGroupNode[] =>
  walkRows(
    props.virtualGrid.filter((r) => r.rowType === "group") as RowGroup[],
    0
  )
);

function walkRows(rows: readonly Row[], depth: number): FlatGroupNode[] {
  const nodes: FlatGroupNode[] = [];

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (props.visibleRowLimit != null && props.visibleRowLimit === rowIndex) {
      nodes.push({
        kind: "show-all",
        remaining: rows.length - rowIndex,
        depth,
      });
      break;
    }

    const row = rows[rowIndex];

    if (row.rowType === "group") {
      const group = row as RowGroup;
      nodes.push({ kind: "group", row: group, depth });
      if (group.isExpanded) {
        nodes.push(...walkRows(group.children, depth + 1));
      }
    }

    if (row.rowType === "content") {
      nodes.push({
        kind: "content",
        row: row as ContentRow,
        depth,
        rowIndex,
      });
    }
  }

  return nodes;
}

/**
 * Indent for the first table column: nesting depth plus placeholders when
 * drag/expander controls are absent so rows still align.
 */
function getTableViewFirstColumnIndentPx(row?: Row, depth: number = 0): number {
  const spacerWidthPixels = 12;
  let spacer = spacerWidthPixels * depth;
  const gridHasExpanders = props.allowExpand || hasGrouping.value;

  // Placeholder when drag is enabled but this row has no drag handle.
  if (props.allowDrag && (!isContentRow(row) || !props.rowDraggable(row))) {
    spacer += 24;
  }

  // Placeholder when expanders exist in the grid but this row has none:
  // header with expanders, non-expandable content in an expandable grid,
  // or content in a grouped (non-expandable) grid.
  if (
    (row === undefined && gridHasExpanders) ||
    (isContentRow(row) && props.allowExpand && !props.rowExpandable(row)) ||
    (isContentRow(row) && !props.allowExpand && gridHasExpanders)
  ) {
    spacer += spacerWidthPixels * 2;
  }

  return spacer;
}
</script>
